import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2, Swords } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { type CodeTorneiValue } from '@/contexts/CodeTorneiContext';
import { SlotPelleCode } from '@/lib/limulo/slotPelleCode';
import { useCodeTorneiPannello } from '@/hooks/useCodeTorneiPannello';
import { useHaptics } from '@/hooks/useHaptics';
import { useRidisegnaAllaScadenza } from '@/hooks/useRidisegnaAllaScadenza';
import {
  QUEUE_EXIT_COPY,
  QUEUE_FORMAT_COPY,
  CODE_GAME_NAMES,
  CR_PROFILE_TYPE,
  formatElapsed,
  matchToOpenOf,
  queueRefusalCopyOf,
  queuesOfKind,
  tutteLeCodeSiOffrono,
  waitingSinceOf,
  type LimuloQueue,
  type LimuloQueueRefusal,
} from '@/lib/limulo/code';
import { limuloFailure } from '@/lib/limulo/errors';
import { externalMatchDeadOf } from '@/lib/limulo/externalData';
import { LIMULO_RESULT_MODE_PROVIDER, matchStateOf } from '@/lib/limulo/match';
import { matchCheckInPlanOf } from '@/lib/limulo/matchActions';
import { presentazioneDi } from '@/lib/limulo/presentazione';
import { attesaProfili, profiloPerIscrizione } from '@/lib/limulo/registration';
import { limuloMatchUrl, limuloMatchesUrl, limuloProfileCreateUrl } from '@/lib/limulo/routes';
import { fraseAttesaOf, segnapostiScadenza } from '@/lib/limulo/scadenzaRisultato';
import { LimuloDegraded } from '@/routes/limulo/LimuloDegraded';
import { useGetLimuloMatchesQuery, useGetLimuloTournamentQuery } from '@/services/limuloApi';
import type { LimuloMatch } from '@/types/limulo';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatOra } from '@/lib/fmt';

/**
 * «Ranked» della Hub di Clash Royale — LE CODE di qTournaments (1.72.0, ADR-0163, contratto kb#282).
 *
 * 📜 Il posto l'ha deciso Luca (2026-09-25 ~17:42Z, via tma-orcb): «le code vanno nella sezione
 * ranked hub di clash royale». Monta SOLO con `VITE_FEATURE_TOURNAMENT_QUEUES_ENABLED` (e Limulo)
 * acceso; a flag spento la scheda resta `RankedPanel` di QDuels com'e' — anche in produzione, dove
 * QDuels e' acceso. La scelta sta in `HubSections.tsx`, in un punto solo.
 *
 * Il biglietto e il rinnovo NON stanno qui: stanno nell'app (`CodeTorneiContext`), perche' cambiare
 * scheda non deve far uscire dalla coda. Qui si legge la lista, si entra, si esce e si racconta.
 *
 * I momenti (`queueMomentOf`): elenco → in attesa (col tempo, «Esci») → abbinato (il torneo si
 * prepara) → partita trovata (all'INCONTRO, dove c'e' il check-in) · uscite non volute come frasi ·
 * rifiuti nominati (il freno con l'ora quando il server la manda).
 */
export function CodeTorneiPanel() {
  const { t } = useTranslation('limulo');
  const { code, gq, lq, profiliQ, crIds, lista, momento, inDiretta, nomi } = useCodeTorneiPannello();

  if (gq.isLoading || (lq.isLoading && lq.data === undefined)) return <PanelWaiting slow={false} />;
  if (lq.error && lq.data === undefined) {
    return (
      <div className="flex flex-col py-2">
        <LimuloDegraded failure={limuloFailure(lq.error)} context="list" />
      </div>
    );
  }
  // Senza il catalogo non si sa quali code siano di Clash Royale: non e' «nessuna coda».
  if (crIds === null || lista === null) return <PanelNotice tone="unavailable" />;

  return (
    <div data-testid="code-tornei-panel" className="flex flex-col gap-3 pt-3">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. E anche il sottotitolo. */}
      <div className="sr-only">
        <span className="text-2xl font-extrabold leading-tight text-fg">
          {t('queues.ui.title', { defaultValue: 'Code' })}
        </span>
        <span className="text-sm font-semibold text-fg-muted">
          {t('queues.ui.subtitle', { defaultValue: 'Clash Royale · entra in coda e gioca subito' })}
        </span>
      </div>

      {momento.kind === 'in-attesa' && (
        <InAttesa
          queues={momento.queues.map((q) => ({
            ...q,
            name: nomi.get(q.queueId)?.name ?? q.queueId,
          }))}
          expiresAfterMs={code.ticket?.expiresAfterMs ?? null}
          busy={code.busy}
          onLeave={() => void code.leave()}
        />
      )}
      {momento.kind === 'abbinato' && <Abbinato />}
      {momento.kind === 'partita-trovata' && (
        <PartitaTrovata
          tournamentId={momento.tournamentId}
          auto={inDiretta}
          checkInMinutes={
            momento.queueId ? (nomi.get(momento.queueId)?.matchCheckInMinutes ?? null) : null
          }
        />
      )}
      {momento.kind === 'uscito' && (
        <p
          data-testid="code-uscita"
          data-reason={momento.reason}
          className="rounded-xl bg-bg-secondary p-3 text-sm text-fg"
        >
          {t(QUEUE_EXIT_COPY[momento.reason])}
        </p>
      )}

      {code.refusal !== null && (
        <Rifiuto
          refusal={code.refusal}
          gameId={lista.queues[0]?.gameId ?? ''}
          ripiego={code.ticket?.lastMatch?.tournamentId ?? null}
        />
      )}
      {code.failure !== null && <LimuloDegraded failure={code.failure} context="list" />}

      {/* L'elenco da FUORI e, sotto la sua carta, a PARTITA TROVATA (L-2436). In attesa e da
          abbinati si e' gia' dentro. A partita trovata il primario resta il check-in, che sta SOPRA:
          l'elenco e' la via d'uscita quando la TMA sbaglia (un torneo che il client crede vivo). Se
          invece e' giusta, il motore rifiuta con `queue.in-game`, e `Rifiuto` lo dice col link al torneo.
          🔴 Le RIGHE sono solo le 1v1 (`coda`): i sit&go stanno nel pannello «Sit&Go» (a flag, `SitngoCodePanel`). Il
          bottone «Entra in tutte le code» invece vale per TUTTO il gioco, sit&go compresi (`tutteLeCodeSiOffrono`, r14):
          «tutte» vuol dire tutte, e il testo sotto lo dice. I NOMI restano quelli di tutta la lista: chi aspetta in un
          sit&go lo legge per nome anche qui (il biglietto e' uno). */}
      {(momento.kind === 'fuori' || momento.kind === 'uscito' || momento.kind === 'partita-trovata') && (
        <Elenco
          queues={queuesOfKind(lista.queues, 'coda')}
          gameId={lista.queues[0]?.gameId ?? ''}
          game={CODE_GAME_NAMES[CR_PROFILE_TYPE]}
          tutte={tutteLeCodeSiOffrono(lista.queues)}
          busy={code.busy}
          profili={profiliQ.data}
          profiliInErrore={profiliQ.isError}
          onJoin={(input) => void code.join(input)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Elenco({
  queues,
  gameId,
  game,
  tutte,
  busy,
  profili,
  profiliInErrore,
  onJoin,
}: {
  queues: LimuloQueue[];
  /** Il gioco di TUTTA la vetrina: le righe qui sono le sole 1v1, e possono essere zero col bottone acceso. */
  gameId: string;
  /** Il nome che il suggerimento dice (`{{game}}`). */
  game: string;
  /** «Entra in tutte le code» si offre: il gioco ha almeno due modelli attivi, di qualunque tipo. */
  tutte: boolean;
  busy: boolean;
  profili: Parameters<typeof profiloPerIscrizione>[0];
  profiliInErrore: boolean;
  onJoin: (input: Parameters<CodeTorneiValue['join']>[0]) => void;
}) {
  const { t } = useTranslation('limulo');
  const haptics = useHaptics();
  const Pelle = useContext(SlotPelleCode);
  // Il vuoto: da solo (nessuna 1v1 e nessun bottone) o sotto il bottone «tutte» (il gioco ha altri modelli, ma non 1v1).
  const vuoto = (
    <p
      data-testid="code-vuote"
      className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted"
    >
      {t('queues.ui.empty', {
        defaultValue: 'Nessuna coda 1v1 aperta per Clash Royale in questo momento.',
      })}
    </p>
  );
  if (queues.length === 0 && !tutte) return vuoto;
  // Il profilo si sceglie come all'iscrizione (kb#237): uno ⇒ quello, piu' d'uno ⇒ il primo.
  // 🔴 E la lista deve aver RISPOSTO: senza, un `queue.profile-required` direbbe «ti manca il
  // profilo» a chi ce l'ha (lo stesso falso di orc39 sull'iscrizione).
  const scelta = profiloPerIscrizione(profili, gameId);
  const stato = attesaProfili(scelta, profiliInErrore);
  const profileId =
    scelta.kind === 'uno'
      ? scelta.profileId
      : scelta.kind === 'piu'
        ? scelta.opzioni[0].profileId
        : null;
  const spento = busy || stato !== 'pronti';
  const entra = (input: { queueId: string } | { anyMode: true }) => {
    haptics.impact('medium');
    onJoin({ gameId, ...input, profileId });
  };

  // Senza «Entra in tutte le code» la riga sotto dice solo lo stato dei profili: la spiegazione del
  // bottone, senza il bottone, non si stampa.
  const statoNota =
    stato === 'in-lettura'
      ? t('queues.ui.readingProfiles', { defaultValue: 'Leggo i tuoi profili di gioco…' })
      : stato === 'illeggibili'
        ? t('queues.ui.profilesUnreadable', {
            defaultValue: 'Non riesco a leggere i tuoi profili di gioco: riprova tra poco.',
          })
        : null;

  if (Pelle?.Elenco) return <Pelle.Elenco vista={{ queues, tutte, busy, spento, statoNota, game, vuoto, entra }} />;
  return (
    <div className="flex flex-col gap-2">
      {tutte && (
        <EntraInTutte
          testId="code-tutte"
          busy={busy}
          disabled={spento}
          onClick={() => entra({ anyMode: true })}
        />
      )}
      {statoNota !== null ? (
        <p className="text-center text-xs text-fg-muted">{statoNota}</p>
      ) : tutte ? (
        <NotaTutte game={game} />
      ) : null}
      {queues.length === 0 ? (
        vuoto
      ) : (
        <ul className="flex flex-col gap-2">
          {queues.map((q) => (
            <li key={q.queueId}>
              <RigaCoda queue={q} disabled={spento} onJoin={() => entra({ queueId: q.queueId })} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * «Entra in tutte le code» (r14): UN bottone, `anyMode: true` senza `kind`, che entra in tutti i modelli attivi del
 * gioco — 1v1 e sit&go. Lo STESSO componente nella «Ranked» e nel «Sit&Go» (`SitngoCodePanel`): il giocatore lo trova
 * dove sta e fa la stessa cosa. `py-3.5 text-base`: la CTA grande del pannello, due override di pelle nominati (PR-4;
 * sono gli stessi del «Play» di QueueSheet).
 */
export function EntraInTutte({
  testId,
  busy,
  disabled,
  onClick,
}: {
  testId: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('limulo');
  return (
    <Button
      peso="bold"
      fullWidth
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className="py-3.5 text-base"
    >
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Swords className="h-5 w-5" />}
      {t('queues.ui.joinAll', { defaultValue: 'Entra in tutte le code' })}
    </Button>
  );
}

/** La spiegazione del bottone: vale per 1v1 E sit&go, si gioca la prima partita che si forma, dalle altre si esce da soli. */
export function NotaTutte({ game }: { game: string }) {
  const { t } = useTranslation('limulo');
  return (
    <p data-testid="code-tutte-nota" className="text-center text-xs text-fg-muted">
      {t('queues.ui.joinAllHint', {
        game,
        defaultValue:
          'Entri in tutte le code aperte di {{game}}, sit&go compresi: giochi la prima partita che si forma e dalle altre esci da solo.',
      })}
    </p>
  );
}

function RigaCoda({
  queue,
  disabled,
  onJoin,
}: {
  queue: LimuloQueue;
  disabled: boolean;
  onJoin: () => void;
}) {
  const { t } = useTranslation('limulo');
  const tipo =
    queue.kind === 'coda'
      ? t('queues.ui.kind1v1', { defaultValue: '1v1' })
      : t('queues.ui.kindSitAndGo', { size: queue.size, defaultValue: 'Sit&go da {{size}}' });
  const formato =
    queue.kind === 'sit-and-go' && queue.format && QUEUE_FORMAT_COPY[queue.format]
      ? t(QUEUE_FORMAT_COPY[queue.format])
      : null;
  return (
    <div
      data-testid="code-riga"
      className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary px-4 py-3"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-bold text-fg">{queue.name}</span>
        <span className="truncate text-xs font-semibold text-fg-muted">
          {[tipo, formato].filter(Boolean).join(' · ')}
        </span>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onJoin}
        className="shrink-0 rounded-lg bg-brand/15 px-3 py-2 text-sm font-bold text-brand active:opacity-80 disabled:opacity-40"
      >
        {t('queues.ui.join', { defaultValue: 'Entra' })}
      </button>
    </div>
  );
}

function InAttesa({
  queues,
  expiresAfterMs,
  busy,
  onLeave,
}: {
  queues: { queueId: string; since: number | null; name: string }[];
  expiresAfterMs: number | null;
  busy: boolean;
  onLeave: () => void;
}) {
  const { t } = useTranslation('limulo');
  const Pelle = useContext(SlotPelleCode);
  const da = waitingSinceOf(queues);
  // L'orologio qui e' SOLO testo: nessun ramo decide niente sul tempo (lo decide il biglietto).
  const [adesso, setAdesso] = useState(() => Date.now());
  useEffect(() => {
    if (da === null) return;
    const h = window.setInterval(() => setAdesso(Date.now()), 1000);
    return () => window.clearInterval(h);
  }, [da]);
  const secondi =
    expiresAfterMs !== null && expiresAfterMs > 0 ? Math.round(expiresAfterMs / 1000) : null;
  if (Pelle?.InAttesa) return <Pelle.InAttesa vista={{ nomi: queues.map((q) => q.name), da, adesso, secondi, busy, onLeave }} />;
  return (
    <div
      data-testid="code-in-attesa"
      className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary px-4 py-5"
    >
      <Loader2 className="h-10 w-10 animate-spin text-brand" />
      <p className="text-base font-bold text-fg">
        {t('queues.ui.inQueue', { defaultValue: 'Sei in coda' })}
      </p>
      <p className="text-center text-sm text-fg-muted">
        {t('queues.ui.inQueueFor', {
          names: queues.map((q) => q.name).join(', '),
          defaultValue: 'Code: {{names}}',
        })}
      </p>
      {da !== null && (
        <p data-testid="code-tempo" className="text-2xl font-extrabold tabular-nums text-fg">
          {formatElapsed(adesso - da)}
        </p>
      )}
      <p className="text-center text-xs text-fg-muted">
        {secondi !== null
          ? t('queues.ui.keepOpen', {
              seconds: secondi,
              defaultValue:
                "Tieni l'app aperta: se resta chiusa più di {{seconds}} secondi esci dalla coda.",
            })
          : t('queues.ui.keepOpenGeneric', {
              defaultValue: "Tieni l'app aperta: se resta chiusa troppo a lungo esci dalla coda.",
            })}
      </p>
      <Button variant="neutro" peso="bold" fullWidth data-testid="code-esci" disabled={busy} onClick={onLeave}>
        {/* `leave()` esce da TUTTO il biglietto del gioco: con piu' code (una 1v1 e un sit&go del
            pannello «Sit&Go») il bottone lo dice, invece di promettere l'uscita da «la» coda. */}
        {queues.length > 1
          ? t('queues.ui.leaveAll', { defaultValue: 'Esci da tutte' })
          : t('queues.ui.leave', { defaultValue: 'Esci dalla coda' })}
      </Button>
    </div>
  );
}

export function Abbinato() {
  const { t } = useTranslation('limulo');
  return (
    <div
      data-testid="code-abbinato"
      className="flex flex-col items-center gap-3 rounded-xl bg-success/15 px-4 py-5"
    >
      <Swords className="h-8 w-8 text-success" />
      <p className="text-base font-bold text-fg">
        {t('queues.ui.matchedTitle', { defaultValue: 'Partita trovata!' })}
      </p>
      <p className="flex items-center gap-2 text-sm text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('queues.ui.matchedPreparing', { defaultValue: 'Il torneo si sta preparando…' })}
      </p>
    </div>
  );
}

/**
 * L'incontro e' DAVVERO in attesa del risultato dal gioco? (I-324)
 * Vero solo con TUTTE e quattro le condizioni, ognuna gia' letta altrove con la stessa regola:
 *  · il referto lo porta il gioco — `resultMode === 'provider'`, uguaglianza esatta (un campo vuoto e'
 *    «non lo so», non `provider`; v. `matchActions.ts`, ramo `provider-result`);
 *  · l'incontro e' `pending` (il passo 5 di `bottoneUnico.ts`): un referto proposto o contestato, o
 *    concluso, NON e' un'attesa del gioco;
 *  · i due check-in risultano fatti — `matchCheckIn.readyAt` valorizzato (`matchCheckInPlanOf`, piano
 *    `pronto` o `chiuso`). Finestra assente o non richiesta ⇒ non lo sappiamo ⇒ la frase di prima;
 *  · la partita presso il provider non e' morta (`externalMatchDeadOf`, come `partitaMorta`).
 * 🔴 La SCADENZA del risultato (kb#343, qT 1.111+, `limulo#206`): la riga dell'incontro porta `resultDeadlineAt`
 * (ISO UTC o `null`) in lista e in dettaglio, e la frase dice FINO A QUANDO (`fraseAttesaOf`,
 * `lib/limulo/scadenzaRisultato.ts`). Prima di kb#343 qui c'era scritto «nessun orologio e nessuna scadenza: la
 * facciata non espone `resultDeadlineAt`»: **non e' piu' vero**, e la frase di sempre resta solo per «non lo so».
 *  · scadenza valida e FUTURA ⇒ «il risultato arriva dal gioco entro le HH:MM» (ora locale, formato della lingua; il
 *    giorno solo se non e' oggi);
 *  · scadenza assente, `null` o illeggibile ⇒ la frase di prima, invariata;
 *  · scadenza PASSATA con l'incontro ancora in attesa (la finestra fra la scadenza e il battito che lo passa allo
 *    staff) ⇒ «il tempo del gioco e' scaduto: a breve decide lo staff» — MAI un orario passato.
 * ⛔ Niente countdown: basta l'ora. Un solo timeout alla scadenza (`useRidisegnaAllaScadenza`) fa passare la frase
 * a «scaduta» a schermo aperto, senza muovere niente al secondo.
 * ⚠️ Limiti noti: (1) `null` vale anche «passato allo staff» (`resultTimedOutAt`): da li' il pannello torna alla
 * frase di prima, anche se ora decide lo staff — serve che la facciata esponga quello stato; (2) il valore vivo
 * NON e' ancora stato visto: la chiave arriva a `null` su ogni riga [M 04/10 22:02Z], quindi la logica e' scritta
 * sul contratto e provata coi doppi, non su un incontro vero.
 */
function inAttesaDelGiocoOf(match: LimuloMatch): boolean {
  if (match.resultMode !== LIMULO_RESULT_MODE_PROVIDER) return false;
  if (matchStateOf(match) !== 'pending') return false;
  if (externalMatchDeadOf(match.externalMatch)) return false;
  const piano = matchCheckInPlanOf(match);
  return (piano.kind === 'pronto' || piano.kind === 'chiuso') && piano.readyAt !== null;
}

/**
 * Partita trovata: si va all'INCONTRO (il check-in e' li'), non alla scheda del torneo.
 * ⭐ Se il passaggio l'abbiamo VISTO accadere (il giocatore aspettava su questa schermata), ci si va
 * da soli: i minuti del check-in sono pochi. Se lo troviamo gia' fatto (pannello riaperto dopo),
 * c'e' il bottone e basta — portare via qualcuno da una schermata che ha appena aperto e' un furto.
 */
export function PartitaTrovata({
  tournamentId,
  auto,
  checkInMinutes,
}: {
  tournamentId: string;
  auto: boolean;
  checkInMinutes: number | null;
}) {
  const { t } = useTranslation('limulo');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const mq = useGetLimuloMatchesQuery({ id: tournamentId });
  const incontro = matchToOpenOf(mq.data, matchStateOf);
  const attesaDelGioco = incontro !== null && inAttesaDelGiocoOf(incontro);
  const lingua = useLinguaFormato();
  // La scadenza conta SOLO nello stato «in attesa del gioco»; `Date.now()` si rilegge a ogni disegno, il timeout
  // singolo (`useRidisegnaAllaScadenza`) ne forza UNO alla scadenza: niente countdown a schermo.
  const scadenza = attesaDelGioco ? (incontro?.resultDeadlineAt ?? null) : null;
  useRidisegnaAllaScadenza(scadenza);
  const frase = attesaDelGioco ? fraseAttesaOf(scadenza, Date.now()) : null;
  const orario =
    frase?.kind === 'entro' ? { ...segnapostiScadenza(frase, lingua), altroGiorno: frase.altroGiorno } : null;
  const destinazione = incontro
    ? limuloMatchUrl(tournamentId, incontro.matchId)
    : limuloMatchesUrl(tournamentId);
  const andato = useRef(false);
  useEffect(() => {
    if (!auto || andato.current || incontro === null) return;
    andato.current = true;
    haptics.notification('success');
    navigate(destinazione);
  }, [auto, incontro, destinazione, navigate, haptics]);

  return (
    <div
      data-testid="code-partita-trovata"
      className="flex flex-col items-center gap-3 rounded-xl bg-success/15 px-4 py-5"
    >
      <Swords className="h-8 w-8 text-success" />
      <p className="text-base font-bold text-fg">
        {t('queues.ui.foundTitle', { defaultValue: 'Partita trovata' })}
      </p>
      <p className="text-center text-sm text-fg">
        {attesaDelGioco
          ? frase?.kind === 'scaduta'
            ? t('queues.ui.foundAwaitingResultExpired', {
                defaultValue: 'Il tempo del gioco è scaduto: a breve decide lo staff.',
              })
            : orario !== null
              ? orario.altroGiorno
                ? t('queues.ui.foundAwaitingResultByDay', {
                    day: orario.day,
                    time: orario.time,
                    defaultValue:
                      'Il risultato arriva dal gioco entro il {{day}} alle {{time}}. Se la partita non si gioca, dall’incontro puoi ritirarti.',
                  })
                : t('queues.ui.foundAwaitingResultBy', {
                    time: orario.time,
                    defaultValue:
                      'Il risultato arriva dal gioco entro le {{time}}. Se la partita non si gioca, dall’incontro puoi ritirarti.',
                  })
              : t('queues.ui.foundAwaitingResult', {
                  defaultValue:
                    'Si attende il risultato dal gioco. Se la partita non si gioca, dall’incontro puoi ritirarti.',
                })
          : checkInMinutes !== null
            ? t('queues.ui.foundCheckin', {
                minutes: checkInMinutes,
                defaultValue:
                  'Hai {{minutes}} minuti per il check-in dell’incontro: se non lo fai perdi a tavolino.',
              })
            : t('queues.ui.foundCheckinGeneric', {
                defaultValue: 'Fai subito il check-in dell’incontro: se non lo fai perdi a tavolino.',
              })}
      </p>
      <Button
        peso="bold"
        fullWidth
        data-testid="code-vai-incontro"
        data-href={destinazione}
        onClick={() => navigate(destinazione)}
        className="py-3.5 text-base"
      >
        {/* r15: una partita di coda o di sit&go non e' un torneo. Una sola frase, anche quando l'incontro non e' ancora
            letto e la rotta e' l'elenco degli incontri: la destinazione non cambia, il testo non nomina il torneo. */}
        {t('queues.ui.goToMatch', { defaultValue: 'Vai alla partita' })}
      </Button>
    </div>
  );
}

export function Rifiuto({
  refusal,
  gameId,
  ripiego = null,
}: {
  refusal: LimuloQueueRefusal;
  gameId: string;
  /**
   * L'ultimo torneo del biglietto (`lastMatch`): se il rifiuto `in-game` non porta il suo, il link va
   * li' — SEMPRE, non solo a partita trovata (2° giro di #496, 🟡 2): il caso che conta e' proprio
   * quello in cui la TMA dice «fuori» e il motore «in gioco».
   */
  ripiego?: string | null;
}) {
  const { t } = useTranslation('limulo');
  const lingua = useLinguaFormato();
  const navigate = useNavigate();
  const torneoDaFinire = refusal.reason === 'queue.in-game' ? (refusal.tournamentId ?? ripiego) : null;
  // r15: il torneo da finire si legge (stessa cache dell'incontro), e da lui — `origine`, con il ripiego sul prefisso — si
  // sa se e' una partita di coda, un sit&go o un torneo vero. Finche' non arriva (o se la lettura fallisce) il testo e' NEUTRO,
  // mai «torneo»: niente lampo di una frase sbagliata, e niente indovinare dal nome.
  const torneoLetto = useGetLimuloTournamentQuery(torneoDaFinire ?? '', { skip: torneoDaFinire === null });
  const presentazione =
    torneoDaFinire !== null && torneoLetto.currentData ? presentazioneDi(torneoDaFinire, torneoLetto.currentData) : null;
  const { key, finoA } = queueRefusalCopyOf(refusal, presentazione);
  const ora = finoA !== null ? formatOra(finoA, lingua) : null;
  return (
    <div
      data-testid="code-rifiuto"
      data-reason={refusal.reason}
      className="flex flex-col gap-2 rounded-xl bg-danger/10 p-3"
    >
      <p className="text-sm text-fg">{ora !== null ? t(key, { time: ora }) : t(key)}</p>
      {/* Solo per `in-game`: il torneo e' quello da FINIRE. Sul freno `tournamentId` e' quello della
          partita saltata, gia' persa: «vai alla partita» porterebbe dove non c'e' piu' niente da fare. */}
      {torneoDaFinire !== null && (
        <Button variant="link" onClick={() => navigate(limuloMatchesUrl(torneoDaFinire))} className="self-start text-sm font-bold">
          {presentazione === 'torneo' || presentazione === 'sfida'
            ? t('queues.ui.goToTournament', { defaultValue: 'Vai al torneo' })
            : presentazione === 'sit-and-go'
              ? t('queues.ui.backToSitngo', { defaultValue: 'Torna al sit&go' })
              : t('queues.ui.backToMatch', { defaultValue: 'Torna alla partita' })}
        </Button>
      )}
      {refusal.reason === 'queue.profile-required' && gameId !== '' && (
        <Button variant="link" onClick={() => navigate(limuloProfileCreateUrl(gameId))} className="self-start text-sm font-bold">
          {t('queues.ui.createProfile', { defaultValue: 'Crea il profilo di gioco' })}
        </Button>
      )}
    </div>
  );
}
